import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Formik, Form } from 'formik'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import { Box, InputLabel, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'

import {
  AppVersion,
  AuthContentWrapper,
  AuthFormWrapper,
  AuthHero,
  HelperWrapper,
  ShowPassowrdIcon,
} from 'src/components'
import { useResetPassword } from 'src/hooks'
import { ResetDtoConfirm } from 'src/types'
import { TrackgoodLogo } from 'src/assets/icons'

import { FONTS, ROUTES, SPACES } from 'src/constants'
import { passwordResetSchema } from 'src/common/validations'
import useStyles from '../styles'

const ResetPassword = () => {
  const [t] = useTranslation()
  const theme = useTheme()
  const { mutateAsync: resetPassword } = useResetPassword()
  const [showPassword, setShowPassword] = useState(false)

  const classes = useStyles()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop'))
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const handleSubmit = async ({ password }: ResetDtoConfirm) => {
    await resetPassword(password)
  }

  const handleShowPassword = () => setShowPassword((show) => !show)

  return (
    <AuthContentWrapper>
      <AuthHero />
      <Grid px={isLaptop ? SPACES.xxxl : 0} item xs className={classes.contentWrapper}>
        <Grid
          display={isLaptop ? 'none' : 'flex'}
          justifyContent="flex-end "
          className={classes.signUpContainer}
        >
          <Typography className={classes.textSignUp} alignItems={'center'}>
            <HelperWrapper t="forgot-password-opposite-text" />{' '}
            <Link style={{ textDecoration: 'none' }} to={ROUTES.LOGIN}>
              <Button className={classes.buttonSignUp}>
                <HelperWrapper t="login-link-text" />
              </Button>
            </Link>
          </Typography>
        </Grid>
        <AuthFormWrapper>
          <Formik
            initialValues={{
              password: '',
              passwordConfirm: '',
            }}
            onSubmit={handleSubmit}
            validationSchema={passwordResetSchema}
          >
            {({ values, handleChange, errors, isSubmitting }) => (
              <Form>
                <Grid container direction="column" justifyContent="center" alignItems="center">
                  <Grid>
                    <TrackgoodLogo className={classes.labelMobile} />
                    <Grid item className={classes.containerTitle}>
                      <Typography
                        marginBottom={SPACES.xxxs}
                        fontWeight={'700'}
                        fontSize={FONTS.SIZES.xxxxl}
                      >
                        <HelperWrapper t="reset-password-title" />
                      </Typography>
                      <Typography
                        display={isLaptop ? 'block' : 'none'}
                        className={classes.textSignUp}
                        alignItems={'center'}
                      >
                        <HelperWrapper t="reset-password-subtitle" />
                      </Typography>
                    </Grid>
                    <Grid item className={classes.containerField}>
                      <InputLabel className={classes.TextInputLabel} id="password">
                        <HelperWrapper t="reset-password-password-input-label" />*
                      </InputLabel>
                      <TextField
                        autoComplete="off"
                        size={isTablet ? 'small' : 'medium'}
                        // inputProps={{
                        //   className: classes.InputProps,
                        // }}
                        InputProps={{
                          endAdornment: (
                            <ShowPassowrdIcon
                              onClick={handleShowPassword}
                              showPassword={showPassword}
                            />
                          ),
                        }}
                        id="password"
                        name="password"
                        placeholder={isTablet ? '' : t('reset-password-password-input-label.v')}
                        // className={classes.TextInput}
                        sx={{
                          width: '100%',
                          '& .MuiFormHelperText-root': {
                            color: 'red',
                          },
                        }}
                        value={values.password}
                        onChange={handleChange}
                        helperText={errors.password && <HelperWrapper t={errors.password} />}
                        type={showPassword ? 'text' : 'password'}
                      />
                    </Grid>
                    <Box my={3} />
                    <Grid item xs={12} className={classes.containerField}>
                      <InputLabel className={classes.TextInputLabel} id="passwordConfirm">
                        <HelperWrapper t="reset-password-password-confirm-input-label" />*
                      </InputLabel>
                      <TextField
                        size={isTablet ? 'small' : 'medium'}
                        autoComplete="off"
                        // inputProps={{
                        //   className: classes.InputProps,
                        // }}
                        InputProps={{
                          endAdornment: (
                            <ShowPassowrdIcon
                              onClick={handleShowPassword}
                              showPassword={showPassword}
                            />
                          ),
                        }}
                        id="passwordConfirm"
                        name="passwordConfirm"
                        placeholder={
                          isTablet ? '' : t('reset-password-password-confirm-input-label.v')
                        }
                        sx={{
                          width: '100%',
                          '& .MuiFormHelperText-root': {
                            color: 'red',
                          },
                        }}
                        // className={classes.TextInput}
                        value={values.passwordConfirm}
                        onChange={handleChange}
                        helperText={
                          errors.passwordConfirm && <HelperWrapper t={errors.passwordConfirm} />
                        }
                        type={showPassword ? 'text' : 'password'}
                      />
                    </Grid>
                    <Box my={2} />
                    <Grid item>
                      <Button
                        disabled={isSubmitting}
                        type="submit"
                        variant="contained"
                        color="secondary"
                        // className={classes.button}
                        sx={{ width: '100%', height: '46px', color: '#fff' }}
                      >
                        <HelperWrapper t="reset-password-submit-button" />
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </AuthFormWrapper>
        <footer className={classes.footerWrapper}>
          <AppVersion />
        </footer>
      </Grid>
    </AuthContentWrapper>
  )
}

export default ResetPassword
